import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMap } from "react-leaflet";
import type { LeafletEventHandlerFnMap, TileEvent } from "leaflet";
import {
  computeTileCoverage,
  tileKey,
  type TileCoverageStatus,
  type TileLoadState,
  type TileRecord,
} from "@/features/map/utils/tileCoverage";

interface Params {
  /** レイヤーが表示中か。false のときは判定を止め、直前に通知していれば null を通知する */
  enabled: boolean;
  /** レイヤーの配信ズーム範囲。地図のズームがこの外なら out-of-zoom を通知する */
  minZoom: number;
  maxZoom: number;
  /** 判定結果の通知先。null は「このレイヤーの通知を消してよい」の意味 */
  onCoverageChange?: (status: TileCoverageStatus | null) => void;
}

/**
 * Leaflet TileLayer のタイル読み込みイベントから、現在の表示範囲にデータがあるかを判定する。
 *
 * 戻り値の eventHandlers を react-leaflet の TileLayer に渡す。判定は
 * 「表示範囲のタイルがすべて読み込み完了した」ことを示す load イベントの時と、
 * 地図のズーム変更時にだけ通知するので、読み込み途中の値がちらつかない。
 */
export const useTileCoverageTracker = ({
  enabled,
  minZoom,
  maxZoom,
  onCoverageChange,
}: Params): LeafletEventHandlerFnMap => {
  const map = useMap();
  const tilesRef = useRef(new Map<string, TileRecord>());
  const currentZRef = useRef<number | null>(null);
  // 直前に通知した値。undefined は一度も通知していない状態
  const lastStatusRef = useRef<TileCoverageStatus | null | undefined>(
    undefined
  );
  const onCoverageChangeRef = useRef(onCoverageChange);
  useEffect(() => {
    onCoverageChangeRef.current = onCoverageChange;
  }, [onCoverageChange]);

  const tracking = enabled && Boolean(onCoverageChange);

  const report = useCallback((status: TileCoverageStatus | null): void => {
    if (lastStatusRef.current === status) return;
    lastStatusRef.current = status;
    onCoverageChangeRef.current?.(status);
  }, []);

  const evaluate = useCallback((): void => {
    const zoom = map.getZoom();
    if (zoom < minZoom || zoom > maxZoom) {
      report("out-of-zoom");
      return;
    }
    report(computeTileCoverage(tilesRef.current.values(), currentZRef.current));
  }, [map, minZoom, maxZoom, report]);

  useEffect(() => {
    if (!tracking) {
      tilesRef.current.clear();
      currentZRef.current = null;
      // 表示していた通知を消す（一度も通知していなければ何もしない）
      if (lastStatusRef.current !== undefined) report(null);
      return;
    }

    evaluate();
    map.on("zoomend", evaluate);
    return () => {
      map.off("zoomend", evaluate);
      report(null);
    };
  }, [tracking, map, evaluate, report]);

  const setTileState = useCallback(
    (event: TileEvent, state: TileLoadState): void => {
      tilesRef.current.set(tileKey(event.coords), { ...event.coords, state });
    },
    []
  );

  return useMemo<LeafletEventHandlerFnMap>(() => {
    if (!tracking) return {};
    return {
      tileloadstart: (event) => {
        currentZRef.current = event.coords.z;
        setTileState(event, "pending");
      },
      tileload: (event) => {
        setTileState(event, "loaded");
      },
      tileerror: (event) => {
        setTileState(event, "error");
      },
      tileunload: (event) => {
        tilesRef.current.delete(tileKey(event.coords));
      },
      tileabort: (event) => {
        tilesRef.current.delete(tileKey(event.coords));
      },
      load: () => {
        evaluate();
      },
    };
  }, [tracking, setTileState, evaluate]);
};
